import type {
  WorkbookColumnarSheet,
  WorkbookDiagnostic,
  WorkbookFreeformSheet,
  WorkbookDiagnosticLocation,
  WorkbookPivotSheet,
  WorkbookSheet,
  WorkbookSheetKind,
} from "@erp/shared-types";
import type { ColumnRead, ColumnReadContext } from "./columnReads";
import { columnarSheetKind } from "./columnar/adapter";
import { freeformSheetKind } from "./freeform/adapter";
import type { ColumnType, FormulaTables } from "./formula";
import type { SheetLineage } from "./lineage";
import type { Schema, SheetPlan } from "./planTypes";
import { pivotSheetKind } from "./pivot/adapter";
import type { WorkbookTableItem } from "./tableReferences";

/** A Result column's Declared type and number format, whatever kind of Worksheet computed it. */
export interface ResultColumn {
  name: string;
  type: ColumnType;
  numberFormat?: string;
}

/** Each Sheet kind's Worksheet type. A kind missing here fails to compile below. */
interface SheetOfKindMap {
  columnar: WorkbookColumnarSheet;
  pivot: WorkbookPivotSheet;
  freeform: WorkbookFreeformSheet;
}
export type SheetOfKind<K extends WorkbookSheetKind> = SheetOfKindMap[K];

/** How each kind of Formula is rewritten into one form (as stored, or as users read it). */
export interface FormulaForms {
  /** A row Formula: a columnar column's, or a Validation's. */
  row: (expression: string) => string;
  /** What was typed into a freeform sheet's cell, at its A1 address. */
  cell: (input: string, address: string) => string;
}

/** What planning one Worksheet reads: every table's known schema and the tables it reads. */
export interface SheetPlanContext {
  schemas: ReadonlyMap<string, Schema>;
  tables: FormulaTables;
  /** Every table a reference can name. */
  items: readonly WorkbookTableItem[];
  /** The ids it reads (its `reads`). */
  dependencies: readonly string[];
}

/**
 * Everything the engine decides by a Worksheet's kind, in one record per kind
 * (ADR 0009). Code outside a kind's adapter never branches on `kind`: it asks
 * `sheetKind(sheet)`, or dispatches with `matchSheetKind`.
 */
export interface SheetKindAdapter<S extends WorkbookSheet> {
  /** The one table it transforms, when its kind reads through one. */
  input(sheet: S): string | undefined;
  /** The ids it reads, in the order its Fingerprint folds them in. */
  reads(sheet: S, tables: readonly WorkbookTableItem[]): string[];
  /** A Diagnostic per table it reads that no Source or Worksheet in `ids` is. */
  missingReads(sheet: S, ids: ReadonlySet<string>): WorkbookDiagnostic[];
  /**
   * Its Result's columns with their Declared types: the Result's own
   * (`columnNames`) when given and its kind names columns from data, else
   * those known without data.
   */
  resultColumns(sheet: S, columnNames?: readonly string[]): ResultColumn[];
  /**
   * The Declared type of a Result column only its data names, when its
   * kind has such columns: a Validation on one is checked when a Run has it.
   */
  dataNamedColumnType(sheet: S): ColumnType | undefined;
  plan(
    sheet: S,
    context: SheetPlanContext,
  ): { plan: SheetPlan; diagnostics: WorkbookDiagnostic[] };
  /** Its Compute-relevant fields, hashed after its id and before what it reads. */
  fingerprintInput(sheet: S): readonly unknown[];
  /** Where its values come from, one table back, given its Plan (`SheetLineage`). */
  lineage(sheet: S, plan: SheetPlan): SheetLineage;
  /**
   * The columns it reads of each table it reads, from its Plan: named
   * columns, or every column where it can't name them. A Run reads only
   * these of a Source.
   */
  columnReads(sheet: S, context: ColumnReadContext): ColumnRead[];
  /** The name of the column, field or key a Diagnostic's part points at, if it still has it. */
  partName(sheet: S, location: WorkbookDiagnosticLocation): string | undefined;
  /** It with each of its kind's own Formulas mapped (Validations are every kind's). */
  mapFormulas(sheet: S, forms: FormulaForms): S;
  /**
   * The number formats its current settings give its Result's columns, by
   * name, for Export presentation; undefined when its Result's own formats stand.
   */
  presentationFormats(
    sheet: S,
  ): ReadonlyMap<string, string | undefined> | undefined;
}

const SHEET_KIND_ADAPTERS: {
  [K in WorkbookSheetKind]: SheetKindAdapter<SheetOfKind<K>>;
} = {
  columnar: columnarSheetKind,
  pivot: pivotSheetKind,
  freeform: freeformSheetKind,
};

/** A Worksheet's kind. A stored Worksheet without one is columnar. */
export function sheetKindOf(sheet: WorkbookSheet): WorkbookSheetKind {
  return sheet.kind ?? "columnar";
}

/** One handler per Sheet kind, each given its kind's Worksheet. */
export type SheetKindHandlers<R> = {
  [K in WorkbookSheetKind]: (sheet: SheetOfKind<K>) => R;
};

/**
 * Calls the handler for a Worksheet's kind. The one switch on `kind`: every
 * per-kind record (backend, frontend) goes through it, so a new kind fails
 * to compile until each record handles it.
 */
export function matchSheetKind<R>(
  sheet: WorkbookSheet,
  handlers: SheetKindHandlers<R>,
): R {
  switch (sheet.kind) {
    case "pivot":
      return handlers.pivot(sheet);
    case "freeform":
      return handlers.freeform(sheet);
    case undefined:
    case "columnar":
      return handlers.columnar(sheet);
  }
}

/** A Worksheet's adapter, bound to it. */
export interface SheetKind {
  kind: WorkbookSheetKind;
  input(): string | undefined;
  reads(tables: readonly WorkbookTableItem[]): string[];
  missingReads(ids: ReadonlySet<string>): WorkbookDiagnostic[];
  resultColumns(columnNames?: readonly string[]): ResultColumn[];
  /** Its output schema known without planning anything upstream. */
  declaredOutput(): Schema;
  dataNamedColumnType(): ColumnType | undefined;
  plan(context: SheetPlanContext): {
    plan: SheetPlan;
    diagnostics: WorkbookDiagnostic[];
  };
  fingerprintInput(): readonly unknown[];
  lineage(plan: SheetPlan): SheetLineage;
  columnReads(context: ColumnReadContext): ColumnRead[];
  partName(location: WorkbookDiagnosticLocation): string | undefined;
  mapFormulas(forms: FormulaForms): WorkbookSheet;
  presentationFormats(): ReadonlyMap<string, string | undefined> | undefined;
}

function bind<S extends WorkbookSheet>(
  adapter: SheetKindAdapter<S>,
  sheet: S,
): SheetKind {
  return {
    kind: sheetKindOf(sheet),
    input: () => adapter.input(sheet),
    reads: (tables) => adapter.reads(sheet, tables),
    missingReads: (ids) => adapter.missingReads(sheet, ids),
    resultColumns: (columnNames) => adapter.resultColumns(sheet, columnNames),
    declaredOutput: () =>
      adapter.resultColumns(sheet).map(({ name, type }) => ({ name, type })),
    dataNamedColumnType: () => adapter.dataNamedColumnType(sheet),
    plan: (context) => adapter.plan(sheet, context),
    fingerprintInput: () => adapter.fingerprintInput(sheet),
    lineage: (plan) => adapter.lineage(sheet, plan),
    columnReads: (context) => adapter.columnReads(sheet, context),
    partName: (location) => adapter.partName(sheet, location),
    mapFormulas: (forms) => adapter.mapFormulas(sheet, forms),
    presentationFormats: () => adapter.presentationFormats(sheet),
  };
}

/** A Worksheet's kind adapter, bound to it. */
export function sheetKind(sheet: WorkbookSheet): SheetKind {
  return matchSheetKind<SheetKind>(sheet, {
    columnar: (columnar) => bind(SHEET_KIND_ADAPTERS.columnar, columnar),
    pivot: (pivot) => bind(SHEET_KIND_ADAPTERS.pivot, pivot),
    freeform: (freeform) => bind(SHEET_KIND_ADAPTERS.freeform, freeform),
  });
}

/**
 * The columns a Worksheet's Result holds, with their Declared types: a
 * columnar Worksheet's Output columns, or a pivot's columns by the pivot
 * layout rule — the Result's own (`columnNames`) when given, since a pivot's
 * Column keys can come from its data, else those known without data.
 */
export function resultColumns(
  sheet: WorkbookSheet,
  columnNames?: readonly string[],
): ResultColumn[] {
  return sheetKind(sheet).resultColumns(columnNames);
}
